import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FlowCard from '../components/FlowCard';
import { useFlowContext } from '../context/FlowContext';
import FileService from '../services/FileService';
import RasaMonitor from '../components/RasaMonitor';

const API_BASE = "http://10.0.9.7:3001";

const Dashboard = () => {
  const navigate = useNavigate();
  const { userId, userGroup, userOrg, setFlowId, setFlowName, autoSaveEnabled, setAutoSaveEnabled } = useFlowContext();
  const [myFlows, setMyFlows] = useState([]);
  const [adminFlows, setAdminFlows] = useState({});
  const [defaultFlows, setDefaultFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRasaMonitor, setShowRasaMonitor] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Fetch user's flows from backend
    fetch(`${API_BASE}/api/flows/${userId}`)
      .then(res => res.json())
      .then(data => {
        setMyFlows(data);
      })
      .catch(() => { });

    // Fetch default flows
    fetch(`${API_BASE}/api/default-flows`)
      .then(res => res.json())
      .then(data => {
        setDefaultFlows(data);
      })
      .catch(() => { });

    // If superadmin, fetch all flows grouped by organisation
    if (userGroup === 'superadmin') {
      fetch(`${API_BASE}/api/admin/flows`)
        .then(res => res.json())
        .then(data => {
          setAdminFlows(data);
          setLoading(false);
        })
        .catch(err => {
          console.error("Failed to load admin flows", err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [userId, userGroup]);

  const handleDeleteFlow = async (flowId, filename) => {
    if (!window.confirm('Are you sure you want to delete this flow?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/flows/${userId}/${filename}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setMyFlows(myFlows.filter(f => f.flow_id !== flowId));
        // Also refresh admin flows if superadmin
        if (userGroup === 'superadmin') {
          const updatedAdminFlows = { ...adminFlows };
          for (const org in updatedAdminFlows) {
            updatedAdminFlows[org] = updatedAdminFlows[org].filter(f => f.flow_id !== flowId);
          }
          setAdminFlows(updatedAdminFlows);
        }
      } else {
        alert('Failed to delete flow.');
      }
    } catch (e) {
      alert('Error deleting flow.');
    }
  };

  const handleDeleteDefaultFlow = async (flowId, filename, superAdminId) => {
    if (!window.confirm('Are you sure you want to delete this default flow?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/flows/${superAdminId}/${filename}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setDefaultFlows(defaultFlows.filter(f => f.flow_id !== flowId));
      } else {
        alert('Failed to delete default flow.');
      }
    } catch (e) {
      alert('Error deleting default flow.');
    }
  };

  const handleBlankFlow = () => {
    // Defer creation: Navigate with 'new' flowId
    setFlowId('new');
    if (setFlowName) setFlowName('');
    navigate('/builder', { state: { flowData: { nodes: {}, edges: [], startNode: null }, flowName: '', flowId: 'new' } });
  };

  const handleOpenFlow = async (flow, isDefault = false) => {
    // For user flows or default flows from DB
    const flowNameToUse = flow.flow_name || flow.name;
    const flowJsonToUse = flow.flow_json;

    if (isDefault) {
      // If it's a default flow, we load its JSON but treat it as a new flow for the current user
      try {
        const flowData = await FileService.getUserFlow(flow.user_id, flow.flow_json);
        // Defer creation: Navigate with 'new' flowId
        setFlowId('new');
        if (setFlowName) setFlowName(flowNameToUse);
        navigate('/builder', { state: { flowData, flowName: flowNameToUse, flowId: 'new' } });
      } catch (e) {
        alert(`Error loading template: ${e.message}`);
      }
    } else {
      // For existing organization flows
      setFlowId(flow.flow_id);
      setFlowName && setFlowName(flow.flow_name);

      if (!flow.flow_json) {
        navigate('/builder', { state: { flowData: { nodes: {}, edges: [], startNode: null }, flowName: flow.flow_name || '', flowId: flow.flow_id } });
        return;
      }

      try {
        const flowData = await FileService.getUserFlow(flow.user_id, flow.flow_json);
        navigate('/builder', { state: { flowData, flowName: flow.flow_name, flowId: flow.flow_id } });
      } catch (error) {
        alert(`Failed to load flow: ${error.message}`);
      }
    }
  };

  return (
    <div className="dashboard" style={{ padding: '2rem 0' }}>
      {/* Dashboard Header with Auto-save Toggle */}
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ margin: 0 }}>Dashboard</h2>
        <div className="auto-save-toggle" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666' }}>Auto-save</span>
          <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
            <input
              type="checkbox"
              checked={autoSaveEnabled}
              onChange={(e) => setAutoSaveEnabled(e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span className="slider round" style={{
              position: 'absolute',
              cursor: 'pointer',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: autoSaveEnabled ? '#6a1b9a' : '#ccc',
              transition: '.4s',
              borderRadius: '24px'
            }}>
              <span style={{
                position: 'absolute',
                content: '""',
                height: '18px',
                width: '18px',
                left: autoSaveEnabled ? '28px' : '4px',
                bottom: '3px',
                backgroundColor: 'white',
                transition: '.4s',
                borderRadius: '50%'
              }}></span>
            </span>
          </label>
        </div>
      </div>

      {/* Default Flows Section */}
      <div className="default-flows-section" style={{ marginBottom: '2.5rem' }}>
        <h3 style={{ marginBottom: '1.2rem' }}>Default Flows</h3>
        {defaultFlows.length === 0 ? (
          <p style={{ color: '#aaa' }}>No default flows available.</p>
        ) : (
          <div className="flows-grid" style={{ gap: '2rem' }}>
            {defaultFlows.map(flow => (
              <FlowCard
                key={flow.flow_id}
                flow={{
                  id: flow.flow_id,
                  name: flow.flow_name,
                  lastModified: flow.created_time
                }}
                onClick={() => handleOpenFlow(flow, true)}
                onDelete={userGroup === 'superadmin' ? () => handleDeleteDefaultFlow(flow.flow_id, flow.flow_json, flow.user_id) : null}
              />
            ))}
          </div>
        )}
      </div>

      {/* Blank Flow Card - Only for Superadmin */}
      {userGroup === 'superadmin' && (
        <div className="blank-flow-section" style={{ marginBottom: '2.5rem' }}>
          <h3 style={{ marginBottom: '1.2rem' }}>Start New</h3>
          <div className="flows-grid" style={{ gap: '2rem' }}>
            <button
              className="blank-flow-btn"
              onClick={handleBlankFlow}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.2rem 2.2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(90deg, #f3e5f5 0%, #ede7f6 100%)',
                boxShadow: '0 2px 12px rgba(44,44,44,0.07)',
                color: '#6a1b9a',
                fontWeight: 600,
                fontSize: '1.15rem',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s, background 0.2s',
                gap: '0.8rem',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'linear-gradient(90deg, #ede7f6 0%, #f3e5f5 100%)'}
              onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(90deg, #f3e5f5 0%, #ede7f6 100%)'}
            >
              <svg width="24" height="24" fill="none" stroke="#6a1b9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem' }}>
                <circle cx="12" cy="12" r="10" stroke="#b39ddb" strokeWidth="2" fill="#fff" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              Blank Flow
            </button>

            <button
              className="blank-flow-btn"
              onClick={() => navigate('/intents')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.2rem 2.2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(90deg, #e3f2fd 0%, #e1f5fe 100%)',
                boxShadow: '0 2px 12px rgba(44,44,44,0.07)',
                color: '#0277bd',
                fontWeight: 600,
                fontSize: '1.15rem',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s, background 0.2s',
                gap: '0.8rem',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'linear-gradient(90deg, #e1f5fe 0%, #e3f2fd 100%)'}
              onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(90deg, #e3f2fd 0%, #e1f5fe 100%)'}
            >
              <svg width="24" height="24" fill="none" stroke="#0277bd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem' }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Manage Intent
            </button>

            <button
              className="blank-flow-btn"
              onClick={() => setShowRasaMonitor(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.2rem 2.2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(90deg, #e8f5e9 0%, #e0f2f1 100%)',
                boxShadow: '0 2px 12px rgba(44,44,44,0.07)',
                color: '#00796b',
                fontWeight: 600,
                fontSize: '1.15rem',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s, background 0.2s',
                gap: '0.8rem',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'linear-gradient(90deg, #e0f2f1 0%, #e8f5e9 100%)'}
              onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(90deg, #e8f5e9 0%, #e0f2f1 100%)'}
            >
              <svg width="24" height="24" fill="none" stroke="#00796b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              RASA Monitor
            </button>
          </div>
        </div>
      )}

      {/* RASA Monitor Modal */}
      {showRasaMonitor && <RasaMonitor onClose={() => setShowRasaMonitor(false)} />}

      {/* My Flows (Organization Flows) */}
      <div className="user-flows-section" style={{ marginBottom: '2.5rem' }}>
        <h3 style={{ marginBottom: '1.2rem' }}>{userOrg ? `${userOrg} Flows` : 'My Flows'}</h3>
        {loading ? (
          <p>Loading...</p>
        ) : myFlows.length === 0 ? (
          <p style={{ color: '#aaa' }}>No flows created yet.</p>
        ) : (
          <div className="flows-grid" style={{ gap: '2rem' }}>
            {myFlows.map(flow => (
              <FlowCard
                key={flow.flow_id}
                flow={{
                  id: flow.flow_id,
                  name: flow.flow_name,
                  lastModified: flow.modified_time || flow.created_time,
                  isOwner: true
                }}
                onClick={() => handleOpenFlow(flow)}
                onDelete={userGroup !== 'user' ? () => handleDeleteFlow(flow.flow_id, flow.flow_json) : null}
              />
            ))}
          </div>
        )}
      </div>

      {/* Superadmin View: All Organization Flows */}
      {userGroup === 'superadmin' && Object.keys(adminFlows).length > 0 && (
        <div className="admin-flows-section">
          <h2 style={{ marginBottom: '1.5rem', borderTop: '1px solid #333', paddingTop: '2rem' }}>All Organization Flows</h2>
          {Object.entries(adminFlows).map(([orgName, flows]) => (
            <div key={orgName} style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: '#888' }}>{orgName}</h4>
              <div className="flows-grid" style={{ gap: '2rem' }}>
                {flows.map(flow => (
                  <FlowCard
                    key={flow.flow_id}
                    flow={{
                      id: flow.flow_id,
                      name: flow.flow_name,
                      lastModified: flow.modified_time || flow.created_time,
                      isOwner: false
                    }}
                    onClick={() => handleOpenFlow(flow)}
                    onDelete={() => handleDeleteFlow(flow.flow_id, flow.flow_json)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard; 

